import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  CheckCircle2,
  TrendingUp,
  Settings,
  ArrowUp,
  Mail,
  Linkedin,
  Menu,
  X,
  Quote,
  Award,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import StickyCTA from '../components/StickyCTA';
import FAQSection from '../components/FAQSection';
import { useSEO } from '../hooks/useSEO';
import { openCalendly } from '../lib/analytics';
import { siteData } from '../mock';

const BRAND_LOGOS = [
  { src: '/brands/good-good.png', alt: 'Good Good' },
  { src: '/brands/food-huggers.webp', alt: 'Food Huggers' },
  { src: '/brands/iprimio.png', alt: 'iPrimio' },
  { src: '/brands/sliptogrip.jpg', alt: 'Slip to Grip' },
  { src: '/brands/touch.webp', alt: 'Touch Skin Care' },
  { src: '/brands/ocushield.webp', alt: 'Ocushield' },
  { src: '/brands/perch.png', alt: 'Perch' },
  { src: '/brands/upscalio.webp', alt: 'Upscalio' },
  { src: '/brands/below-zero.png', alt: 'Below Zero' },
  { src: '/brands/premium-guard.png', alt: 'Premium Guard' },
];

/**
 * A reusable, SEO-first landing page template.
 * Takes a page config (see landingConfigs.js) and renders a full page with unique
 * hero + body copy + FAQ, plus shared trust sections (marquee, testimonials,
 * pricing, founder, final CTA).
 */
const LandingPage = ({ config, source }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const navigate = useNavigate();

  useSEO({
    title: config.seo.title,
    description: config.seo.description,
    keywords: config.seo.keywords,
    canonicalPath: config.path,
    hreflang: config.seo.hreflang || [],
    jsonLd: config.jsonLd,
  });

  React.useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [config.path]);

  const cta = (label = 'Book a Free Strategy Call', src) => (
    <Button
      onClick={() => openCalendly(src || source)}
      size="lg"
      data-testid={`cta-${(src || source).replace(/\s+/g, '-')}`}
      className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
    >
      {label}
    </Button>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`landing-${source}`}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo-link">
            <img src="/logo.png" alt="First Page Consultants" className="h-10 sm:h-11 w-auto" />
            <span className="hidden sm:inline-block font-extrabold tracking-tight text-foreground text-base lg:text-lg">
              First Page <span className="text-primary">Consultants</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            <Link to="/" className="text-muted-foreground hover:text-primary transition-colors font-medium">Home</Link>
            <Link to="/amazon-ppc-agency" className="text-muted-foreground hover:text-primary transition-colors font-medium">PPC Agency</Link>
            <Link to="/amazon-consulting-services" className="text-muted-foreground hover:text-primary transition-colors font-medium">Consulting</Link>
            <Link to="/#services" className="text-muted-foreground hover:text-primary transition-colors font-medium">Services</Link>
            <ThemeToggle />
            <Button
              onClick={() => openCalendly(source + '_header')}
              className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
            >
              Book a Strategy Call
            </Button>
          </nav>
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-foreground hover:text-primary"
              aria-label="Open menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden px-4 pb-4 space-y-2 border-t border-border">
            <Link to="/" className="block px-4 py-2 rounded-lg hover:bg-accent">Home</Link>
            <Link to="/amazon-ppc-agency" className="block px-4 py-2 rounded-lg hover:bg-accent">PPC Agency</Link>
            <Link to="/amazon-ads-agency-usa" className="block px-4 py-2 rounded-lg hover:bg-accent">Amazon Ads (USA)</Link>
            <Link to="/amazon-ads-agency-canada" className="block px-4 py-2 rounded-lg hover:bg-accent">Amazon Ads (Canada)</Link>
            <Link to="/amazon-consulting-services" className="block px-4 py-2 rounded-lg hover:bg-accent">Consulting</Link>
            <Button
              onClick={() => openCalendly(source + '_mobile_menu')}
              className="w-full bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold"
            >
              Book a Strategy Call
            </Button>
          </div>
        )}
      </header>

      <main id="main">
        {/* Hero */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-28 bg-gradient-to-b from-[hsl(var(--brand-soft))] to-background">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07] dark:opacity-[0.12]"
            style={{
              backgroundImage:
                'linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(ellipse at top, black 40%, transparent 75%)',
              WebkitMaskImage: 'radial-gradient(ellipse at top, black 40%, transparent 75%)',
            }}
          />
          <div className="container mx-auto px-4 sm:px-6 relative">
            <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-up">
              <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                {config.hero.eyebrow}
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1]">
                {config.hero.h1}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                {config.hero.sub}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                {cta('Book a Free Strategy Call', source + '_hero')}
                <Button
                  onClick={() => navigate('/#services')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-border hover:border-primary hover:text-primary bg-transparent font-semibold"
                >
                  See How We Work
                </Button>
              </div>
              <p className="text-sm font-semibold text-foreground pt-2">{config.hero.trust}</p>
              <div className="flex flex-wrap gap-2 sm:gap-3 pt-4 justify-center">
                {siteData.hero.credibility.map((item, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="px-3 py-1.5 bg-accent text-accent-foreground hover:bg-accent text-xs sm:text-sm font-medium border border-border"
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Brands Marquee */}
        <section className="py-10 sm:py-14 bg-background border-y border-border">
          <div className="container mx-auto px-4 sm:px-6">
            <p className="text-center text-sm text-muted-foreground mb-6 uppercase tracking-wider font-semibold">
              Trusted by growing Amazon brands
            </p>
            <div className="relative overflow-hidden">
              <div className="marquee-mask-left absolute left-0 top-0 bottom-0 w-16 sm:w-32 z-10" />
              <div className="marquee-mask-right absolute right-0 top-0 bottom-0 w-16 sm:w-32 z-10" />
              <div className="flex gap-8 animate-marquee">
                {[...BRAND_LOGOS, ...BRAND_LOGOS].map((b, i) => (
                  <div
                    key={`${b.alt}-${i}`}
                    className="brand-chip flex-shrink-0 w-40 h-20 flex items-center justify-center rounded-xl px-3"
                  >
                    <img
                      src={b.src}
                      alt={`${b.alt} — Amazon brand client of First Page Consultants`}
                      loading="lazy"
                      decoding="async"
                      className="max-h-12 w-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Body / unique content */}
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10 sm:mb-14 space-y-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                  {config.bodyHeadline}
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
                  {config.bodyIntro}
                </p>
              </div>
              <div className="space-y-4 sm:space-y-5">
                {config.bodyPoints.map((pt, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-5 sm:p-6 bg-card rounded-xl border border-border hover-lift"
                  >
                    <div className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 bg-primary/10 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-foreground mb-1">{pt.h}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {pt.p}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-12">
                {cta('Book a Free Strategy Call', source + '_body')}
                <p className="text-xs text-muted-foreground mt-3">
                  No obligation. Clear direction on the first call.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 sm:py-20 bg-[hsl(var(--surface-subtle))]">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                  Operators, not order-takers
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground mt-3">
                  Real feedback from Amazon brands we run PPC and operations for.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {siteData.testimonials.map((t, i) => (
                  <Card key={i} className="relative bg-card border border-border hover-lift overflow-hidden">
                    <div
                      className="absolute top-0 left-0 right-0 h-1"
                      style={{
                        background:
                          'linear-gradient(90deg, hsl(var(--brand)), hsl(var(--brand-hover)))',
                      }}
                    />
                    <CardContent className="p-6 flex flex-col h-full">
                      <Quote className="w-8 h-8 text-primary/30 mb-4" />
                      <p className="text-foreground/90 text-base leading-relaxed flex-1">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                      <div className="mt-6 pt-6 border-t border-border flex items-center gap-4">
                        <div className="brand-chip w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                          <img src={t.logo} alt={`${t.brand} logo`} loading="lazy" className="max-h-8 max-w-10 object-contain" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-foreground truncate">{t.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {t.title} · <span className="text-primary font-semibold">{t.brand}</span>
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-14 sm:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
                  Simple, transparent pricing
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  No upsells. No hidden fees. No percentage-of-spend games.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="relative border border-border hover-lift bg-card overflow-hidden">
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                  <CardHeader className="text-center pb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-foreground mb-2">PPC Management</CardTitle>
                    <div className="mt-2">
                      <span className="text-4xl font-black text-primary">$799</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </CardHeader>
                  <CardContent className="text-center">
                    <ul className="space-y-3 text-left">
                      {['Full Amazon PPC management', 'Daily optimization & monitoring', 'Weekly performance reports', 'Sponsored Products, Brands & Display'].map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground/90">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => openCalendly(source + '_pricing_ppc')}
                      className="w-full mt-6 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border border-border hover-lift bg-card">
                  <CardHeader className="text-center pb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-foreground mb-2">
                      Account & Listing Optimization
                    </CardTitle>
                    <div className="mt-2">
                      <span className="text-4xl font-black text-primary">$500</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </CardHeader>
                  <CardContent className="text-center">
                    <ul className="space-y-3 text-left">
                      {['Complete account setup & optimization', 'Listing optimization for conversions', 'Keyword research & content strategy', 'Brand Registry & account health'].map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground/90">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => openCalendly(source + '_pricing_listing')}
                      className="w-full mt-6 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Proof / stats */}
        <section className="py-14 bg-[hsl(var(--surface-subtle))]">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {siteData.proof.map((item, i) => (
                <div key={i} className="text-center p-6 bg-card rounded-xl border border-border">
                  <Award className="w-10 h-10 text-primary mx-auto mb-3" />
                  <p className="text-sm font-semibold text-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder mini */}
        <section className="py-14 sm:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto grid md:grid-cols-[240px_1fr] gap-8 items-center">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border max-w-[240px]">
                <img
                  src={siteData.founder.image}
                  alt={`${siteData.founder.name}, ${siteData.founder.title} at First Page Consultants`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{siteData.founder.name}</h2>
                <p className="text-primary font-semibold mb-4">{siteData.founder.title}</p>
                <p className="text-muted-foreground mb-6">{siteData.founder.bio}</p>
                {cta('Book a Strategy Call with Hitesh', source + '_founder')}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQSection
          headline="Frequently Asked Questions"
          subhead="What Amazon brands ask us before booking a strategy call."
          faqs={config.faqs}
          source={source + '_faq'}
        />

        {/* Final CTA */}
        <section
          className="relative py-16 sm:py-20 text-primary-foreground overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--brand)) 0%, hsl(var(--brand-hover)) 100%)',
          }}
        >
          <div className="container mx-auto px-4 sm:px-6 text-center max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to run Amazon like a real revenue channel?
            </h2>
            <p className="text-lg opacity-95 mb-8">
              Book a free strategy call. Clear direction on the first call. No obligation.
            </p>
            <Button
              onClick={() => openCalendly(source + '_final')}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 font-semibold"
            >
              Book a Free Strategy Call
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[hsl(222,47%,8%)] dark:bg-[hsl(222,47%,4%)] text-white py-10 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="First Page Consultants" className="h-10 w-auto" />
                <span className="font-extrabold text-white">First Page <span className="text-primary">Consultants</span></span>
              </div>
              <p className="text-sm text-white/70">{siteData.footer.description}</p>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Mail className="w-4 h-4 text-primary" />
                <a href="mailto:teams@firstpageconsultants.com" className="hover:text-white">
                  teams@firstpageconsultants.com
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Linkedin className="w-4 h-4 text-primary" />
                <a href="https://www.linkedin.com/company/first-page-consultants" target="_blank" rel="noopener noreferrer" className="hover:text-white">
                  LinkedIn
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Landing Pages</h4>
              <div className="flex flex-col space-y-2 text-sm text-white/70">
                <Link to="/amazon-ppc-agency" className="hover:text-white">Amazon PPC Agency</Link>
                <Link to="/amazon-ppc-agency-north-america" className="hover:text-white">PPC Agency · North America</Link>
                <Link to="/amazon-ads-agency-usa" className="hover:text-white">Amazon Ads Agency USA</Link>
                <Link to="/amazon-ads-agency-canada" className="hover:text-white">Amazon Ads Agency Canada</Link>
                <Link to="/best-amazon-ppc-agency" className="hover:text-white">Best Amazon PPC Agency</Link>
                <Link to="/amazon-consulting-services" className="hover:text-white">Amazon Consulting Services</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <p className="text-sm text-white/70 mb-4">{siteData.footer.location}</p>
              <Button
                onClick={() => openCalendly(source + '_footer')}
                className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold"
              >
                Get in Touch
              </Button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center text-sm text-white/50">
            &copy; {new Date().getFullYear()} {siteData.footer.companyName}. All rights reserved.
          </div>
        </div>
      </footer>

      <StickyCTA source={source} />

      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 z-40 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground p-3 rounded-full shadow-lg hidden md:block"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default LandingPage;
